import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import currencyFormatter from 'currency-formatter';
import Button from 'yoda-core-components/lib/components/Button/Button';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import withReducerSaga from '../../../hoc/ReducerRegistry/withReducerSaga';
import {
    watchActiveCartDraftOrderSaga,
    watchActiveCartBestCouponSaga,
    watchApplyBestCouponOnActiveCartSaga,
} from '../../../sagas/ActiveCartReminderSaga';
import ActiveCartDraftOrderReducer from '../../../reducers/ActiveCartDraftOrderReducer';
import ActiveCartBestCouponReducer from '../../../reducers/ActiveCartBestCouponReducer';
import ActiveCartToggleWiggleReducer from '../../../reducers/ActiveCartToggleWiggleReducer';
import ActiveCartReminderAction from '../../../actions/ActiveCartReminderAction';
import getActiveCartDetails, {
    getActiveCartContentDetails,
} from '../../../selectors/ActiveCartReminderSelector';
import * as styles from './ActiveCartReminder.css';
import {
    getLastCartVisitTime,
    setLastCartVisitTime,
} from '../../../helpers/Utils/activeCartLastCartVisitTime';
import { CART_PAGE_URL } from '../../../common/Constants';
import analyticsActions from '../../../actions/AnalyticsAction';

const cx = classNames.bind(styles);
let wiggleIntervalId = null;

export class ActiveCartReminder extends Component {
    static propTypes = {
        activeCartDetails: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        cartContent: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        actions: PropTypes.object,
        activeCartConfig: PropTypes.number,
        toggleWiggleCart: PropTypes.bool,
        cartStyleName: PropTypes.string,
    };

    static defaultProps = {
        activeCartDetails: {},
        cartContent: {},
        actions: {},
        activeCartConfig: {},
        toggleWiggleCart: {},
        cartStyleName: '',
    };

    componentDidMount() {
        const currTime = new Date();
        const lastCartVisitTimeinLS = getLastCartVisitTime();
        /* istanbul ignore next */
        if (isEmpty(lastCartVisitTimeinLS)) {
            setLastCartVisitTime();
        }

        const lastCartVisitTime = new Date(getLastCartVisitTime()).getTime();
        const {
            reminderRefreshTimerInSec,
            initialWiggleAfterLoadInSec,
            wiggleIntervalInSec,
            activeCartContentDisplayTimeInSec,
            cartWiggleTimeInSec,
        } = this.props.activeCartConfig;

        /* istanbul ignore next */
        if (
            lastCartVisitTime > 0 &&
            currTime.getTime() - lastCartVisitTime > reminderRefreshTimerInSec * 1000
        ) {
            this.props.actions.getDraftOrderDetails();
            setTimeout(() => {
                this.props.actions.toggleWiggleCart({
                    wiggleCart: true,
                    showActiveCartModal: true,
                });
                this.props.actions.triggerActiveCartReminder();
                setTimeout(() => {
                    this.props.actions.toggleWiggleCart({
                        wiggleCart: false,
                        showActiveCartModal: false,
                    });
                }, activeCartContentDisplayTimeInSec * 1000);
                wiggleIntervalId = setInterval(() => {
                    this.props.actions.toggleWiggleCart({
                        wiggleCart: true,
                        showActiveCartModal: false,
                    });
                    setTimeout(() => {
                        this.props.actions.toggleWiggleCart({
                            wiggleCart: false,
                            showActiveCartModal: false,
                        });
                    }, cartWiggleTimeInSec * 2 * 1000);
                }, wiggleIntervalInSec * 1000);
            }, initialWiggleAfterLoadInSec * 1000);
            setLastCartVisitTime();
        }
    }

    componentWillUnmount() {
        clearInterval(wiggleIntervalId);
    }

    goToCartPage = () => {
        const { activeCartDetails } = this.props;
        /* istanbul ignore else */
        if (activeCartDetails.bestCouponAvailable) {
            this.props.actions.applyBestCoupon(activeCartDetails.activeCartContent.couponName);
        } else {
            window.location.pathname = CART_PAGE_URL;
        }
    };

    render() {
        const { toggleWiggleCart, cartContent, cartStyleName, activeCartDetails } = this.props;
        const activeCartReminderStyles = dt([
            'p-0',
            'sm:z-[2]',
            'text-black',
            'font-open-sans-regular',
            'text-small',
            'text-center',
        ]);

        const activeCartTooltipBottomStyles = dt([
            'inline-block',
            'absolute',
            'bg-white',
            'sm:shadow-[0_8px_16px_0_rgba(83,96,107,0.5)]',
            '-right-3',
            'sm:top-[59px]',
            'py-3',
            'pl-5',
            'pr-3',
            'sm:rounded-[4px]',
            'sm:z-[2]',
            'sm:w-[221px]',
            'smMd:min-w-[152px]',
            "before:content-['']",
            'before:block',
            'before:absolute',
            'before:w-0',
            'before:h-0',
            'before:border-solid',
            'before:border-transparent',
            'before:border-[9px]',
            'before:-top-[9px]',
            'before:right-6',
            'before:-ml-2.5',
            'before:border-t-0',
            'before:border-b-gray-25',
            'before:z-[2]',
            "after:content-['']",
            'after:block',
            'after:absolute',
            'after:w-0',
            'after:h-0',
            'after:border-solid',
            'after:border-transparent',
            'after:border-8',
            'after:-top-2',
            'after:right-[25px]',
            'after:-ml-2.5',
            'after:border-t-0',
            'after:border-b-white',
            'after:z-[2]',
        ]);

        const tooltipContentStyles = dt([
            'sm:min-h-[24px]',
            'text-sm',
            'leading-7',
            'text-center',
            'text-black',
            'sm:min-w-[58px]',
        ]);
        const tooltipContentHeaderStyles = dt([
            'font-open-sans-bold',
            'text-xlarge',
            'text-gray-70',
            'mb-2',
        ]);

        const contentTextStyles = dt([
            'font-open-sans-regular',
            'text-small',
            'text-gray-70',
            'mb-3',
        ]);

        const cartDiscountStyles = dt(['text-brand-red', 'font-bold']);
        return (
            <>
                {toggleWiggleCart.showActiveCartModal ? (
                    <div className={`${cx('activeCartReminder')} ${activeCartReminderStyles}`}>
                        <div className={activeCartTooltipBottomStyles}>
                            <div className={`${cx('tooltipContent')} ${tooltipContentStyles}`}>
                                <h2 className={tooltipContentHeaderStyles}>Checkout Now</h2>
                                <p>
                                    <img
                                        className={`${cx('activeCartImg')} ${dt(['hidden'])}`}
                                        src={activeCartDetails.itemImageURL}
                                        alt="Active Cart Reminder"
                                    />
                                </p>
                                <p className={contentTextStyles}>
                                    {activeCartDetails.activeCartContent.discount && (
                                        <span>
                                            Save{' '}
                                            <span className={cartDiscountStyles}>
                                                {currencyFormatter.format(
                                                    activeCartDetails.activeCartContent.discount,
                                                    { code: 'USD', precision: 2 }
                                                )}
                                            </span>
                                        </span>
                                    )}
                                    <span>{cartContent.context}</span>
                                </p>
                                <Button
                                    type="button"
                                    onClick={this.goToCartPage}
                                    automationId="activeCart-reminder"
                                    buttonType="Primary"
                                    className={cx('activeCartBtn')}
                                >
                                    {cartContent.buttonText}
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    activeCartDetails: getActiveCartDetails(state),
    cartContent: getActiveCartContentDetails(state),
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({ ...ActiveCartReminderAction, ...analyticsActions }, dispatch),
});

export default compose(
    withReducerSaga(
        [ActiveCartDraftOrderReducer, ActiveCartBestCouponReducer, ActiveCartToggleWiggleReducer],
        [
            watchActiveCartDraftOrderSaga,
            watchActiveCartBestCouponSaga,
            watchApplyBestCouponOnActiveCartSaga,
        ]
    ),
    connect(mapStateToProps, mapDispatchToProps)
)(ActiveCartReminder);
